import { gtin as gtinCheckDigit } from "cdigit";
import { interpretScan } from "./gs1/interpretGS1Scan.js";

const gs1BarcodeTypes = ["LABEL-TYPE-QRCODE", "LABEL-TYPE-EAN128"];
const symbologyIdRegExp = /^\][A-Za-z][0-9A-Za-z]/; // e.g. ]C1 (GS1-128), ]Q3 (QR Code), ]d2 (DataMatrix)
const onlyGtinRegExp = /^(\d{8})$|^(\d{12,14})$/; // Plain GTIN without Application Identifier

// GS1 dates with day 00 (returned as YYYY-MM) mean the last day of the month: e.g. 2026-12 -> 2026-12-31, 2028-02 -> 2028-02-29
function getFullDate(value) {
	let yearMonth = /^(\d{4})-(\d{2})$/.exec(value || "");
	if (!yearMonth) return value || null;
	let lastDay = new Date(Date.UTC(parseInt(yearMonth[1]), parseInt(yearMonth[2]), 0)).getUTCDate();
	return value + "-" + String(lastDay).padStart(2, "0");
}

function decodeValue(value) {
	try {
		return decodeURIComponent(value);
	} catch (error) {
		return value;
	}
}

/*
	Checks if the barcode is a GS1 with Application Identifiers and extracts GTIN (01), BATCH/LOT (10),
	BEST BEFORE (15), EXPIRY (17) and SERIAL (21). Dates are returned in ISO format (YYYY-MM-DD; day 00 is the last day of the month).
	Result is true only if the barcode type is LABEL-TYPE-QRCODE or LABEL-TYPE-EAN128 and the GS1 tools validate it as a GS1 barcode.
*/
export async function getGs1ApplicationIdentifiers(barcode, type) {
	let gs1Data = {
		result: false,
		gtin: null,
		batch: null,
		bestBefore: null,
		expiry: null,
		serial: null,
		errorMessage: null,
	};

	if (!gs1BarcodeTypes.includes(type) || !barcode) return gs1Data;

	let scan = barcode.replace(symbologyIdRegExp, "");
	if (onlyGtinRegExp.test(scan)) return gs1Data;

	try {
		let interpretation = await interpretScan(scan);
		if (interpretation.errmsg) {
			gs1Data.errorMessage = String(interpretation.errmsg);
			return gs1Data;
		}
		interpretation.ol.forEach(element => {
			switch (element.ai) {
				case "01": gs1Data.gtin = decodeValue(element.value); break;
				case "10": gs1Data.batch = decodeValue(element.value); break;
				case "15": gs1Data.bestBefore = getFullDate(element.value); break;
				case "17": gs1Data.expiry = getFullDate(element.value); break;
				case "21": gs1Data.serial = decodeValue(element.value); break;
			}
		});
	} catch (error) {
		gs1Data.errorMessage = String(error);
		return gs1Data;
	}

	gs1Data.result = true;
	return gs1Data;
}

/*
	Returns the barcode to search for an item read as GS1: the 12 central digits of the GTIN-14 (excluding the
	indicator digit and the check digit) with the recalculated check digit, e.g. 18012345678904 -> 8012345678907.
	Returns null if the GTIN is not a valid GTIN-14.
*/
export function getGs1SearchBarcode(gtin) {
	if (!gtin || !/^\d{14}$/.test(gtin)) return null;
	return gtinCheckDigit.generate(gtin.substring(1, 13));
}

/*
	Returns how the lot/serial of an item must be managed (only if manageGs1Ai is true):
	- type: 'lot' (item.has_lots), 'serial' (item.has_serials) or null (no lot/serial management)
	- code: the lot/serial code from the GS1 data (BATCH/LOT (10) wins over SERIAL (21)), null if not present
	- expiryDate: only for lots, the date from the GS1 data (BEST BEFORE (15) or EXPIRY (17)), null if not present
	gs1Data is null when the item was selected from the list instead of read by barcode
*/
export function getItemTracking(item, gs1Data, manageGs1Ai) {
	let itemTracking = {
		type: null,
		code: null,
		expiryDate: null,
	};

	if (manageGs1Ai != true || !item) return itemTracking;
	if (item.has_lots == true) itemTracking.type = "lot";
	else if (item.has_serials == true) itemTracking.type = "serial";
	else return itemTracking;

	if (gs1Data) {
		itemTracking.code = gs1Data.batch || gs1Data.serial || null;
		if (itemTracking.type == "lot") itemTracking.expiryDate = gs1Data.bestBefore || gs1Data.expiry || null;
	}
	return itemTracking;
}

// Returns the date part (YYYY-MM-DD) of a date selected with SpotDate, null if empty
export function getDateOnly(value) {
	if (!value) return null;
	if (value instanceof Date) return value.getFullYear() + "-" + String(value.getMonth() + 1).padStart(2, "0") + "-" + String(value.getDate()).padStart(2, "0");
	return String(value).substring(0, 10);
}

/*
	Returns the barcode to search for an item after a reading, and the GS1 data to keep:
	- manageGs1Ai false: the barcode as read (as before GS1 management)
	- GS1 with GTIN (01): the barcode from getGs1SearchBarcode() and the GS1 data
	- LABEL-TYPE-EAN128, or a valid GS1 without GTIN (01): result false, the reading must be signaled as error
	- other types, or a QR Code that is not a GS1: the barcode as read
*/
export function getBarcodeToSearch(barcode, type, gs1Data, manageGs1Ai) {
	let barcodeToSearch = {
		result: true,
		barcode: barcode,
		gs1Data: null,
	};

	if (manageGs1Ai != true) return barcodeToSearch;

	let gs1SearchBarcode = (gs1Data && gs1Data.result == true) ? getGs1SearchBarcode(gs1Data.gtin) : null;
	if (gs1SearchBarcode) {
		barcodeToSearch.barcode = gs1SearchBarcode;
		barcodeToSearch.gs1Data = gs1Data;
	}
	else if (type == "LABEL-TYPE-EAN128" || (gs1Data && gs1Data.result == true)) {
		barcodeToSearch.result = false;
	}
	return barcodeToSearch;
}
